import { compact, head, uniqBy } from "lodash";
import pluralize from "pluralize";
import { useMemo } from "react";
import {
  AssessmentCalibrationQuestionFragment,
  CalibrationNodeFragment,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";

import { getQuestionLabels } from "../questions/question";

const CalibratedQuestionList = ({
  allCalibrations,
  questions,
  onSelectQuestion,
  onScrollToQuestion,
}: {
  allCalibrations: CalibrationNodeFragment[];
  questions: AssessmentCalibrationQuestionFragment[];
  onSelectQuestion?: (questionId: number) => void;
  onScrollToQuestion?: (questionId: number) => void;
}) => {
  const calibrationDataByQuestionId = useMemo(() => {
    const questions = uniqBy(
      compact(allCalibrations.map((calibration) => calibration.question)),
      (question) => question.id,
    );
    return questions.reduce(
      (acc, question) => {
        return {
          ...acc,
          [question.id]: {
            latestEdit: head(
              allCalibrations.filter(
                (calibration) =>
                  calibration.question?.id === question.id &&
                  !calibration.isSuggestion,
              ),
            ),
            commentCount: allCalibrations.filter(
              (calibration) =>
                calibration.question?.id === question.id &&
                calibration.isSuggestion,
            ).length,
          },
        };
      },
      {} as Record<
        number,
        {
          latestEdit: CalibrationNodeFragment | undefined;
          commentCount: number;
        }
      >,
    );
  }, [allCalibrations]);

  return questions.map((question) => {
    const questionLabels =
      question.__typename === "RangeAssessmentQuestionNode" ||
      question.__typename === "TalentIndicatorAssessmentQuestionNode"
        ? getQuestionLabels(question)
        : {};
    return (
      <div
        key={question.id}
        className={classNames(
          "p-4 rounded-lg flex items-center justify-between",
          !!onSelectQuestion && "hover:bg-gray-100 hover:cursor-pointer",
        )}
        role={onSelectQuestion ? "button" : undefined}
        onClick={() => {
          if (onSelectQuestion) {
            onSelectQuestion(question.id);
          }
        }}
      >
        <div>
          <div className="tf-header-sm-dark">{question.title}</div>
          <div className="text-xs flex items-center gap-2">
            {calibrationDataByQuestionId[question.id].latestEdit && (
              <div className="flex items-center gap-1 text-amber-700">
                <div>Calibrated answer:</div>
                <div className="font-medium flex items-center gap-1">
                  {
                    questionLabels[
                      calibrationDataByQuestionId[question.id].latestEdit
                        ?.integerAnswer || 0
                    ]
                  }
                  {questionLabels[
                    calibrationDataByQuestionId[question.id].latestEdit
                      ?.integerAnswer || 0
                  ] !==
                    `${
                      calibrationDataByQuestionId[question.id].latestEdit
                        ?.integerAnswer
                    }` &&
                    ` (${
                      calibrationDataByQuestionId[question.id].latestEdit
                        ?.integerAnswer
                    })`}
                </div>
              </div>
            )}
            {calibrationDataByQuestionId[question.id].latestEdit &&
              calibrationDataByQuestionId[question.id].commentCount > 0 && (
                <TopicflowIcon type="dot" className="h-3 w-3 text-gray-400" />
              )}
            {calibrationDataByQuestionId[question.id].commentCount > 0 && (
              <div className="text-gray-400">{`${
                calibrationDataByQuestionId[question.id].commentCount
              } ${pluralize(
                "comment",
                calibrationDataByQuestionId[question.id].commentCount,
              )}`}</div>
            )}
          </div>
        </div>
        {!!onScrollToQuestion && (
          <Button
            icon={TopicflowIconMap.chevronDownDouble}
            theme={buttonTheme.iconGray}
            onClick={() => onScrollToQuestion(question.id)}
          />
        )}
      </div>
    );
  });
};

export default CalibratedQuestionList;
